import '@ant-design/v5-patch-for-react-19'
import '../../src/instrument'
import '../../src/i18n'
import '../../src/index.css'

import {
  type ErrorComponentProps,
  Outlet,
  createRootRouteWithContext,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { Button } from 'antd'
import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import type { AuthContext, RouterContext } from '../../src/router-context'
import { NotFound } from '../../src/pages/Core/NotFound'
import { useMountEffect } from '../../src/hooks/common/useMountEffect'
import { captureBrowserException } from '../../src/monitoring/glitchtip'
import { AppProvider } from '../../src/providers/AppProvider'
import { useAuth } from '../../src/hooks/auth/useAuth'
import { parseWxLoginParams, useWxLogin } from '../../src/hooks/auth/useWxLogin'
import { isWxMiniProgram, redirectToMiniProgram } from '../../src/utils/wx'
import { MainLayout } from '../../src/components/Layout/MainLayout'
import { fetchApi, type ApiResponse } from '../../src/hooks/common/useFetcher'

const fallbackAuth: AuthContext = {
  isAuthenticated: false,
  permissions: [],
}

interface RawPermission {
  permission_value: string
}

interface RawUserAuthority {
  permissions: RawPermission[]
}

async function loadAuth(): Promise<AuthContext> {
  try {
    const userInfoRes = await fetchApi('getUserInfo') as ApiResponse
    if (userInfoRes.status !== 0) return fallbackAuth

    const authRes = await fetchApi('getUserAuthority') as ApiResponse<RawUserAuthority>
    const permissions = authRes.data?.permissions?.map((p) => p.permission_value) ?? []
    return { isAuthenticated: true, permissions }
  } catch {
    return fallbackAuth
  }
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context }) => {
    // 使用 ensureQueryData 缓存 auth，避免每次导航都发网络请求
    const auth = await context.queryClient.ensureQueryData({
      queryKey: ['auth'],
      queryFn: loadAuth,
      staleTime: 5 * 60 * 1000, // 5 分钟内复用缓存
    })
    return { auth }
  },
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
})

function RootComponent() {
  return (
    <AppProvider>
      <ProfileOutlet />
    </AppProvider>
  )
}

function ProfileOutlet() {
  const { profile } = Route.useRouteContext()
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  if (profile === 'wx-webview') {
    return (
      <WxLoginGate>
        <Outlet />
      </WxLoginGate>
    )
  }

  // PC 端：公开/重定向页面不显示导航栏，其余页面均显示
  const isPublicRoute =
    pathname === '/login' ||
    pathname === '/unauthorized' ||
    pathname === '/authRedirect' ||
    pathname.startsWith('/picc/auth-redirect') ||
    pathname.startsWith('/kbt-map/auth-redirect') ||
    pathname.startsWith('/picc/cargo-redirect')

  if (isPublicRoute) {
    return <Outlet />
  }

  return <MainLayout />
}

function WxLoginGate({ children }: { children: ReactNode }) {
  if (typeof window === 'undefined') {
    return children
  }

  return <BrowserWxLoginGate>{children}</BrowserWxLoginGate>
}

function BrowserWxLoginGate({ children }: { children: ReactNode }) {
  const { isLogined } = useAuth()
  const { status: wxLoginStatus } = useWxLogin()
  const inMiniProgram = isWxMiniProgram()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const navigate = useNavigate()
  const isOnLoginPage = pathname === '/login'

  useMountEffect(() => {
    const hasWxLoginParams = !!parseWxLoginParams()
    if (isLogined || hasWxLoginParams || isOnLoginPage) return

    if (inMiniProgram && wxLoginStatus === 'idle') {
      redirectToMiniProgram('/pages/login/login')
      return
    }

    if (!inMiniProgram) {
      void navigate({ to: '/login', replace: true })
    }
  })

  if (isLogined || isOnLoginPage || !inMiniProgram) {
    return children
  }

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-white">
      <Loader2 className="size-8 animate-spin text-primary" />
      <div className="mt-3 text-sm text-gray-500">
        {wxLoginStatus === 'loading' ? '正在登录...' : '正在跳转...'}
      </div>
    </div>
  )
}

export function NotFoundComponent() {
  return <NotFound />
}

export function ErrorComponent({ error }: ErrorComponentProps) {
  useMountEffect(() => {
    captureBrowserException(error, {
      tags: { error_boundary: 'tanstack_root_route' },
    })
  })

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-white p-4">
      <div className="w-full max-w-4xl text-center">
        <h1 className="text-xl font-semibold">很抱歉，似乎发生了一些问题</h1>
        <Button className="mt-4" onClick={() => window.location.reload()}>
          刷新页面
        </Button>
        <pre className="mt-8 max-h-96 overflow-auto rounded-md border bg-gray-100 px-4 py-2 text-left text-xs">
          {error instanceof Error ? error.stack : String(error)}
        </pre>
      </div>
    </main>
  )
}
